import React, { useMemo } from 'react';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import { resolveKitBox } from './equipmentSilhouettePorts';
import { getEquipmentKitWorldTransform } from './fabKitWorldTransform';
import FabEquipmentDetailLayer3D from './FabEquipmentDetailLayer3D';
import FabAmhsInstances3D from './FabAmhsInstances3D';
import { useDynamicEquipmentIds, useEquipmentDetailIds } from './EquipmentLod3D';
import { useAppSelector } from '../../hooks/redux';
import type { LayoutElement } from '../../types';
export type FabAmhsFloorLayer3DProps = {
  descriptors: EquipmentRenderDescriptor[];
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
  onSelect?: (id: string) => void;
  /** Override max full-kit count (camera + selection capped). */
  maxDetailed?: number;
};

function buildLodCandidates(descriptors: EquipmentRenderDescriptor[]) {
  return descriptors.map((d) => {
    const box = resolveKitBox(d);
    const t = getEquipmentKitWorldTransform(d);
    const worldSize = Math.max(box.w, box.h, box.d, 1);
    return {
      id: d.id,
      worldX: t.x,
      worldY: t.y + box.h * 0.5,
      worldZ: t.z,
      worldSize,
    };
  });
}

export const FabAmhsFloorLayer3D: React.FC<FabAmhsFloorLayer3DProps> = React.memo(
  ({ descriptors, layoutMeta, onSelect, maxDetailed: maxDetailedProp }) => {
    const selectedLayoutIds = useAppSelector((s) => s.selection.selectedIds);
    const selectedInstanceId = useAppSelector((s) => s.equipment.selectedInstanceId);

    const candidateIds = useMemo(() => descriptors.map((d) => d.id), [descriptors]);
    const transferDetailIds = useDynamicEquipmentIds(candidateIds);

    const forceIds = useMemo(() => {
      const forced = new Set<string>();
      for (const id of selectedLayoutIds) forced.add(id);
      if (selectedInstanceId) forced.add(selectedInstanceId);
      for (const id of transferDetailIds) forced.add(id);
      return forced;
    }, [selectedLayoutIds, selectedInstanceId, transferDetailIds]);

    const lodCandidates = useMemo(() => buildLodCandidates(descriptors), [descriptors]);

    const maxDetailed =
      maxDetailedProp ?? Math.min(10, Math.max(4, Math.ceil(descriptors.length * 0.12)));

    const detailIds = useEquipmentDetailIds(lodCandidates, {
      forceIds,
      maxDetailed,
      promoteByCamera: true,
    });

    if (descriptors.length === 0) return null;

    return (
      <group name="FabAmhsFloorLayer3D">
        <FabAmhsInstances3D
          descriptors={descriptors}
          excludeIds={detailIds}
          layoutMeta={layoutMeta}
          onSelect={onSelect}
        />
        <FabEquipmentDetailLayer3D
          descriptors={descriptors}
          detailIds={detailIds}
          layoutMeta={layoutMeta}
          onSelect={onSelect}
        />
      </group>
    );
  }
);

FabAmhsFloorLayer3D.displayName = 'FabAmhsFloorLayer3D';

export default FabAmhsFloorLayer3D;
